import { apiFetcher } from "@/services/API.service";
import {
  BasicTask,
  BasicTaskSchema,
  EditedTask,
  FavoriteSearch,
  FavoriteSearchSchema,
  FullTask,
  FullTaskSchema,
  NewTask,
  SearchTask,
  SearchTaskAdvanced,
  SearchTaskBasic,
  SearchTaskBasicSchema,
  SearchTaskSchema,
  SingleAuditLog,
  SingleAuditLogSchema,
} from "@/types";
import {
  validateArrayWithSchema,
  validateWithSchema,
} from "@/utils/dataValidators.zod";
import { z } from "zod";

// #region createTask
// Create task
export async function createTask(task: NewTask): Promise<
  | {
      rStatus: "success";
      taskID: number;
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    const response = await apiFetcher({
      route: "/task/create",
      method: "POST",
      options: {
        body: {
          assigned_to: task.assigned_to,
          title: task.title,
          due_date: task.due_date,
          input: task.input,
          collaborators: task.collaborators,
          tags: task.tags,
          parent_id: task.parent_id ? task.parent_id : null,
        },
      },
    });
    if (response.status === 200) {
      if (
        !response.data ||
        typeof response.data !== "object" ||
        !("task_id" in response.data) ||
        typeof response.data.task_id !== "number"
      ) {
        return { rStatus: "error", message: "invalid response" };
      }

      return { rStatus: "success", taskID: response.data.task_id };
    }

    if (response.status === 400) {
      return { rStatus: "error", message: "bad request" };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", message: "unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", message: "route not found" };
    }

    return { rStatus: "error", message: "failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "failed to fetch" };
  }
}
// #endregion createTask

// #region getUserTasks
// Get all tasks for the current user
export async function getUserTasks({
  signal,
  retry = false,
}: {
  signal?: AbortSignal;
  retry?: boolean;
}): Promise<
  | {
      rStatus: "success";
      tasks: BasicTask[];
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    if (retry) {
      console.log("Retrying getUserTasks");
    }

    const response = await apiFetcher({
      route: "/user/tasks",
      method: "POST",
      signal: signal,
    });

    if (response.status === 200) {
      const validatedData = validateArrayWithSchema<BasicTask>(
        response.data,
        BasicTaskSchema,
        "Basic Task"
      );

      if (!validatedData) {
        return {
          rStatus: "error",
          message: "invalid response: some tasks are not valid",
        };
      }

      return {
        rStatus: "success",
        tasks: validatedData,
      };
    }

    if (response.status === 204) {
      return { rStatus: "success", tasks: [] };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", message: "unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", message: "route not found" };
    }

    if (response.status === 0) {
      if (response.error === "request_aborted") {
        return { rStatus: "error", message: "request_aborted" };
      }

      // retry fetching the tasks
      if (!retry) {
        return getUserTasks({ signal, retry: true });
      }
      return {
        rStatus: "error",
        message: "network_error",
      };
    }
    return { rStatus: "error", message: "failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "failed to fetch" };
  }
}

// #endregion getUserTasks

// #region getTask
// Get a single task by ID
export async function fetchTask({
  taskID,
  signal,
  retry = false,
}: {
  taskID: number;
  signal?: AbortSignal;
  retry?: boolean;
}): Promise<
  | {
      rStatus: "success";
      task: FullTask;
    }
  | {
      rStatus: "error";
      message:
        | "unauthorized"
        | "network_error"
        | "request_aborted"
        | (string & {});
    }
> {
  try {
    if (retry) {
      console.log("Retrying fetchTask");
    }

    const response = await apiFetcher({
      route: `/user/task`,
      method: "POST",
      options: {
        body: {
          task_id: taskID,
        },
      },
      signal: signal,
    });

    if (response.status === 200) {
      const validatedData = validateWithSchema<FullTask>(
        response.data,
        FullTaskSchema,
        "Full Task"
      );

      if (!validatedData) {
        return {
          rStatus: "error",
          message: "invalid response: task is not valid",
        };
      }

      return {
        rStatus: "success",
        task: validatedData,
      };
    }

    if (response.status === 204) {
      return { rStatus: "error", message: "Task not found" };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", message: "unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", message: "Route not found" };
    }

    if (response.status === 0) {
      if (response.error === "request_aborted") {
        return { rStatus: "error", message: "request_aborted" };
      }

      // retry fetching the tasks
      if (!retry) {
        return fetchTask({ taskID, signal, retry: true });
      }
      return {
        rStatus: "error",
        message: "network_error",
      };
    }

    return { rStatus: "error", message: "Failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "Failed to fetch" };
  }
}
// #endregion getTask

// #region updateTask
// Update a task
export async function updateTask({ task }: { task: EditedTask }): Promise<
  | {
      rStatus: "success";
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    const response = await apiFetcher({
      route: "/task/update",
      method: "POST",
      options: {
        body: {
          id: task.id,
          status: task.status,
          title: task.title,
          due_date: task.due_date,
          input: task.input,
          collaborators: task.collaborators,
          tags: task.tags,
          parent_id: task.parent_id ? task.parent_id : undefined,
          assigned_to: task.assigned_to,
        },
      },
    });

    if (response.status === 200 || response.status === 202) {
      return { rStatus: "success" };
    }

    if (response.status === 400) {
      return { rStatus: "error", message: "bad request" };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", message: "unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", message: "route not found" };
    }

    return { rStatus: "error", message: "failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "failed to fetch" };
  }
}
// #endregion updateTask

// #region deleteTask
// Delete a task
export async function sendDeleteTasks({
  taskIDs,
}: {
  taskIDs: number[];
}): Promise<
  | {
      rStatus: "success";
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    const response = await apiFetcher({
      route: "/task/delete",
      method: "POST",
      options: {
        body: {
          task_ids: taskIDs,
        },
      },
    });

    if (response.status === 200 || response.status === 202) {
      return { rStatus: "success" };
    }

    if (response.status === 400) {
      return { rStatus: "error", message: "bad request" };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", message: "unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", message: "route not found" };
    }

    return { rStatus: "error", message: "failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "failed to fetch" };
  }
}
// #endregion deleteTask

// #region updateTaskStatus
// Update a task's status
export async function updateTaskStatus({
  taskID,
  status,
}: {
  taskID: number;
  status: string;
}): Promise<
  | {
      rStatus: "success";
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    const response = await apiFetcher({
      route: "/task/update/status",
      method: "POST",
      options: {
        body: {
          id: taskID,
          status: status,
        },
      },
    });

    if (response.status === 200 || response.status === 202) {
      return { rStatus: "success" };
    }

    if (response.status === 400) {
      return { rStatus: "error", message: "bad request" };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", message: "unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", message: "route not found" };
    }

    return { rStatus: "error", message: "failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "failed to fetch" };
  }
}
// #endregion updateTaskStatus

// #region searchTasks
// Search tasks

export async function allSearchTasks({
  all,
  signal,
  retry = false,
}: {
  // LAST 6 MONTHS
  all: boolean;
  signal?: AbortSignal;
  retry?: boolean;
}): Promise<
  | {
      rStatus: "success";
      tasks: SearchTask[];
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    if (retry) {
      console.log("Retrying allSearchTasks");
    }

    const response = await apiFetcher({
      route: "/user/tasks/search",
      method: "POST",
      options: {
        body: {
          all: all,
        },
      },
      signal: signal,
    });

    if (response.status === 200) {
      const validatedData = validateArrayWithSchema<SearchTask>(
        response.data,
        SearchTaskSchema,
        "Search Task"
      );

      if (!validatedData) {
        return {
          rStatus: "error",
          message: "invalid response: some tasks are not valid",
        };
      }

      return {
        rStatus: "success",
        tasks: validatedData,
      };
    }

    if (response.status === 204) {
      return { rStatus: "success", tasks: [] };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", message: "unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", message: "route not found" };
    }

    if (response.status === 0) {
      if (response.error === "request_aborted") {
        return { rStatus: "error", message: "request_aborted" };
      }

      // retry fetching the tasks
      if (!retry) {
        return allSearchTasks({ all, signal, retry: true });
      }

      return {
        rStatus: "error",
        message: "network_error",
      };
    }

    return { rStatus: "error", message: "failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "failed to fetch" };
  }
}

// Add to search history
export async function addFavoriteSearch({
  search,
  name,
}: {
  search: string;
  name: string;
}): Promise<
  | {
      rStatus: "success";
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    const response = await apiFetcher({
      route: "/search/add",
      method: "POST",
      options: {
        body: {
          search: search,
          name: name,
        },
      },
    });

    console.log(response);

    if (response.status === 200 || response.status === 202) {
      return { rStatus: "success" };
    }

    if (response.status === 400) {
      return { rStatus: "error", message: "bad request" };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", message: "unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", message: "route not found" };
    }

    return { rStatus: "error", message: "failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "failed to fetch" };
  }
}

// Delete favorite search
export async function deleteFavoriteSearch(id: number): Promise<
  | {
      rStatus: "success";
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    const response = await apiFetcher({
      route: "/favourites/search/delete",
      method: "POST",
      options: {
        body: {
          id: id,
        },
      },
    });

    console.log(response);

    if (
      response.status === 200 ||
      response.status === 202 ||
      response.status === 204
    ) {
      return { rStatus: "success" };
    }

    if (response.status === 400) {
      return { rStatus: "error", message: "bad request" };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", message: "unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", message: "route not found" };
    }

    return { rStatus: "error", message: "failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "failed to fetch" };
  }
}

// Fetch search history
export async function fetchFavoriteSearches({
  retry = false,
  signal,
}: {
  retry?: boolean;
  signal?: AbortSignal;
}): Promise<
  | {
      rStatus: "success";
      history: FavoriteSearch[];
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    const response = await apiFetcher({
      route: "/favourites/search/fetch",
      method: "POST",
      signal: signal,
    });
    console.log(response);

    if (response.status === 200) {
      const validatedData = validateArrayWithSchema<FavoriteSearch>(
        response.data,
        FavoriteSearchSchema,
        "Favorite Search"
      );

      if (!validatedData) {
        return {
          rStatus: "error",
          message: "invalid response: some tasks are not valid",
        };
      }

      return {
        rStatus: "success",
        history: validatedData,
      };
    }

    if (response.status === 204) {
      return { rStatus: "success", history: [] };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", message: "unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", message: "route not found" };
    }

    if (response.status === 0) {
      if (response.error === "request_aborted") {
        return { rStatus: "error", message: "request_aborted" };
      }

      // retry fetching the tasks
      if (!retry) {
        return fetchFavoriteSearches({
          retry: true,
        });
      }

      return {
        rStatus: "error",
        message: "network_error",
      };
    }

    return { rStatus: "error", message: "failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "failed to fetch" };
  }
}

// #endregion searchTasks

// #region basic and advanced search tasks

// Basic search tasks - fetches only basic task data
export async function basicSearchTasks({
  all,
  signal,
  retry = false,
}: {
  all: boolean;
  signal?: AbortSignal;
  retry?: boolean;
}): Promise<
  | {
      rStatus: "success";
      tasks: SearchTaskBasic[];
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    if (retry) {
      console.log("Retrying basicSearchTasks");
    }

    const response = await apiFetcher({
      route: "/user/tasks/search",
      method: "POST",
      options: {
        body: {
          all: all,
          fetchAdvancedData: false,
        },
      },
      signal: signal,
    });

    if (response.status === 200) {
      const validatedData = validateArrayWithSchema<SearchTaskBasic>(
        response.data,
        SearchTaskBasicSchema,
        "Search Task Basic"
      );

      if (!validatedData) {
        return {
          rStatus: "error",
          message: "invalid response: some tasks are not valid",
        };
      }

      return {
        rStatus: "success",
        tasks: validatedData,
      };
    }

    if (response.status === 204) {
      return { rStatus: "success", tasks: [] };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", message: "unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", message: "route not found" };
    }

    if (response.status === 0) {
      if (response.error === "request_aborted") {
        return { rStatus: "error", message: "request_aborted" };
      }

      // retry fetching the tasks
      if (!retry) {
        return basicSearchTasks({ all, signal, retry: true });
      }

      return {
        rStatus: "error",
        message: "network_error",
      };
    }

    return { rStatus: "error", message: "failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "failed to fetch" };
  }
}

// Advanced search tasks - fetches full task data with history, timers, etc.
export async function advancedSearchTasks({
  all,
  signal,
  retry = false,
}: {
  all: boolean;
  signal?: AbortSignal;
  retry?: boolean;
}): Promise<
  | {
      rStatus: "success";
      tasks: SearchTaskAdvanced[];
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    if (retry) {
      console.log("Retrying advancedSearchTasks");
    }

    const response = await apiFetcher({
      route: "/user/tasks/search",
      method: "POST",
      options: {
        body: {
          all: all,
          fetchAdvancedData: true,
        },
      },
      signal: signal,
    });

    if (response.status === 200) {
      const validatedData = validateArrayWithSchema<SearchTaskAdvanced>(
        response.data,
        SearchTaskSchema,
        "Search Task Advanced"
      );

      if (!validatedData) {
        return {
          rStatus: "error",
          message: "invalid response: some tasks are not valid",
        };
      }

      return {
        rStatus: "success",
        tasks: validatedData,
      };
    }

    if (response.status === 204) {
      return { rStatus: "success", tasks: [] };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", message: "unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", message: "route not found" };
    }

    if (response.status === 0) {
      if (response.error === "request_aborted") {
        return { rStatus: "error", message: "request_aborted" };
      }

      // retry fetching the tasks
      if (!retry) {
        return advancedSearchTasks({ all, signal, retry: true });
      }

      return {
        rStatus: "error",
        message: "network_error",
      };
    }

    return { rStatus: "error", message: "failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "failed to fetch" };
  }
}

// #endregion basic and advanced search tasks

// #region sendCopyTask
export async function sendCopyTask({ taskID }: { taskID: number }): Promise<
  | {
      rStatus: "success";
      taskID: number;
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    const response = await apiFetcher({
      route: "/task/copy",
      method: "POST",
      options: {
        body: {
          task_id: taskID,
        },
      },
    });

    if (response.status === 200) {
      if (
        !response.data ||
        typeof response.data !== "object" ||
        !("task_id" in response.data) ||
        typeof response.data.task_id !== "number"
      ) {
        return { rStatus: "error", message: "invalid response" };
      }

      return { rStatus: "success", taskID: response.data.task_id };
    }

    if (response.status === 400) {
      return { rStatus: "error", message: "bad request" };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", message: "unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", message: "route not found" };
    }

    return { rStatus: "error", message: "failed to send" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "failed to send" };
  }
}

// #endregion sendCopyTask

// #region sendUpdatedSearchFavorites
export async function sendUpdatedSearchFavorites({
  searches,
}: {
  searches: FavoriteSearch[];
}): Promise<
  | {
      rStatus: "success";
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    const response = await apiFetcher({
      route: "/favourites/search/order/update",
      method: "POST",
      options: {
        body: {
          favourite_orders: searches,
        },
      },
    });

    if (response.status === 200 || response.status === 202) {
      return { rStatus: "success" };
    }

    if (response.status === 400) {
      return { rStatus: "error", message: "bad request" };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", message: "unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", message: "route not found" };
    }

    return { rStatus: "error", message: "failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "failed to fetch" };
  }
}
// #endregion sendUpdatedSearchFavorites

// #region sendConvertTaskToSubTask
export async function sendConvertTaskToSubTask({
  taskID,
  newMainTaskID,
}: {
  taskID: number;
  newMainTaskID: number;
}): Promise<
  | {
      rStatus: "success";
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    const response = await apiFetcher({
      route: "/task/update",
      method: "POST",
      options: {
        body: {
          id: taskID,
          parent_id: newMainTaskID,
        },
      },
    });

    if (response.status === 200 || response.status === 202) {
      return { rStatus: "success" };
    }

    if (response.status === 204) {
      return { rStatus: "error", message: "Task not found" };
    }

    if (response.status === 409) {
      return {
        rStatus: "error",
        message:
          "Cannot convert this task to subtask of this task's subtasks. Creates circular dependency",
      };
    }

    if (response.status === 400) {
      return { rStatus: "error", message: "Bad request" };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", message: "Unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", message: "Route not found" };
    }

    if (response.status === 500) {
      return { rStatus: "error", message: "Internal server error" };
    }

    return { rStatus: "error", message: "Failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "Failed to fetch" };
  }
}
// #endregion sendConvertTaskToSubTask

export async function sendConvertSubTaskToMainTask({
  taskID,
}: {
  taskID: number;
}): Promise<
  | {
      rStatus: "success";
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    const response = await apiFetcher({
      route: "/task/update",
      method: "POST",
      options: {
        body: {
          id: taskID,
          parent_id: null,
        },
      },
    });

    if (response.status === 200 || response.status === 202) {
      return { rStatus: "success" };
    }

    if (response.status === 204) {
      return { rStatus: "error", message: "Task not found" };
    }

    if (response.status === 400) {
      return { rStatus: "error", message: "bad request" };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", message: "unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", message: "route not found" };
    }

    if (response.status === 500) {
      return { rStatus: "error", message: "internal server error" };
    }

    return { rStatus: "error", message: "failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "failed to fetch" };
  }
}
// #endregion sendConvertSubTaskToMainTask

// #region fetchTaskActivityLogs
export async function fetchTaskActivityLogs({
  taskID,
  signal,
}: {
  taskID: number;
  signal?: AbortSignal;
}): Promise<
  | {
      rStatus: "success";
      rData: SingleAuditLog[];
    }
  | {
      rStatus: "error";
      rMessage: string;
    }
> {
  try {
    const response = await apiFetcher({
      route: `/user/task`,
      method: "POST",
      options: {
        body: {
          task_id: taskID,
        },
      },
      signal: signal,
    });

    const ResponseSingleAuditLogsSchema = z.object({
      audit_logs: z.array(SingleAuditLogSchema),
    });

    if (response.status === 200) {
      const validatedData = validateWithSchema(
        response.data,
        ResponseSingleAuditLogsSchema,
        "Single Audit Log"
      );
      if (!validatedData) {
        return {
          rStatus: "error",
          rMessage: "invalid response: some logs are not valid",
        };
      }
      return {
        rStatus: "success",
        rData: validatedData.audit_logs,
      };
    }
    if (response.status === 204) {
      return { rStatus: "success", rData: [] };
    }
    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", rMessage: "unauthorized" };
    }
    if (response.status === 404) {
      return { rStatus: "error", rMessage: "route not found" };
    }
    return { rStatus: "error", rMessage: "failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", rMessage: "failed to fetch" };
  }
}

// #endregion fetchTaskActivityLogs
